import { Button, Form, FormInstance, Image, Input, message, Space, Spin, Typography } from 'antd'
import AupAction from './actions/AupAction'
import React, { useCallback, useEffect, useState } from 'react'
import DataPromptAction from './actions/DataPromptAction'

import {
  ActionInput,
  EnrollmentStepConfig, LdapInput, StepConfig, useLazyGetCurrentStepQuery, useLazyGetWorkflowStepQuery,
  useLdapLoginMutation,
  useUpdateWorkflowStepMutation
} from './api/enrollement';
import { BaseAction } from './types/BaseAction';
import { AupConfig } from './types/AupActionConfig';
import DisplayMessageAction from './actions/DisplayMessageAction';
import { DisplayMessageActionConfig } from './types/DisplayMessageActionConfig';
import { UIColorSchema, UIStyleSchema } from './types/UIConfiguration';
import { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import { UserInfoConfig } from './types/UserInfoConfig';
import { DpskAction } from './actions/DpskAction';
import { MacRegActionConfig } from './types/MacRegActionConfig';
import MacRegAction from './actions/MacRegAction';
import { MacRegOnboarded } from './actions/MacRegOnboarded';
import { Wifi4euBanner } from './icons';
import { useIntl } from 'react-intl'
import { CertAction } from './actions/CertAction';
import UserSplitAction from './actions/UserSplitAction';
import { UserSplitActionConfig, UserSplitConfig } from './types/UserSplitConfig';
import { SAMLActionConfig } from './types/SAMLActionConfig';
import SAMLAction from './actions/SAMLAction';
import LdapAction from './actions/LDAPAction';
import { LDAPActionConfig } from './types/LDAPActionConfig';
import {PassphraseOnboarded} from './actions/PassphraseOnboarded';
import CaptivePortalAction from './actions/CaptivePortalAction';

function Action(actionType: string, stepType: string, config: BaseAction, variables: any, form: FormInstance, setDisableContinue: React.Dispatch<React.SetStateAction<boolean>>, onContinue: Function, stepConfig: any, ldapFormLoading?: boolean) {

  if (stepType === 'end') {
    switch (actionType) {
      case 'DPSK':
        return <DpskAction data={variables} />
      case 'CERT_TEMPLATE':
        return <CertAction data={variables} />
      case 'MAC_REG':
        return <MacRegOnboarded data={variables} />
      case 'PASSPHRASE':
        return <PassphraseOnboarded data={variables} />
      case 'CAPTIVE_PORTAL':
        return <CaptivePortalAction data={variables} />  
      default:
        return null;
    }
  } else if (stepType === 'split') {
    switch (actionType) {
      case 'USER_SELECTION_SPLIT':
        return <UserSplitAction config= {{actionConfig: config as UserSplitActionConfig, stepConfig: stepConfig } as UserSplitConfig} form={form} setDisableContinue={setDisableContinue} onContinue = {onContinue}/>
    }
  } else {
    switch (actionType) {
      case 'AUP':
        return <AupAction config={config as AupConfig} form={form} setDisableContinue={setDisableContinue} onContinue= {onContinue} />
      case 'DISPLAY_MESSAGE':
        return <DisplayMessageAction config={config as DisplayMessageActionConfig} form={form} setDisableContinue={setDisableContinue}  onContinue= {onContinue}/>
      case 'DATA_PROMPT':
        return <DataPromptAction config={config as UserInfoConfig} form={form} setDisableContinue={setDisableContinue}  onContinue= {onContinue}/>
      case 'MAC_REG':
        return <MacRegAction config={config as MacRegActionConfig} form={form} setDisableContinue={setDisableContinue}  onContinue= {onContinue}/>
      case 'IDP_AUTH_PROVIDER':
         return <SAMLAction config={config as SAMLActionConfig} form={form} setDisableContinue={setDisableContinue}  onContinue= {onContinue} />
      case 'LDAP_AUTH_PROVIDER':
         return <LdapAction formLoading={!!ldapFormLoading}/>
      default:
        return null
    } 
  }

}

const EnrollmentForm = ({ uiColorConfig, uiStyleConfig, firstActionStepId }: {
  uiColorConfig: UIColorSchema,
  uiStyleConfig: UIStyleSchema,
  firstActionStepId: string
}) => {
  const { $t } = useIntl()
  const [stepId, setStepId] = useState<string>('')
  const [actionType, setActionType] = useState<string>('')
  const [disableContinue, setDisableContinue] = React.useState(false)
  const [getStep, { isLoading: isLoadingStep, isSuccess, data: stepConfig }] = useLazyGetWorkflowStepQuery()
  const [actionConfig, setActionConfig] = useState<BaseAction>()
  const [updateStep, { isLoading: isUpdatingStep }] = useUpdateWorkflowStepMutation()
  const [form] = useForm<ActionInput>()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [direction, setDirection] = useState<'next' | 'prev'>('next')
  const [ getCurrentStep, { data: currentStepConfig } ] = useLazyGetCurrentStepQuery()
  const [variables, setVariables] = useState()
  const [stepType, setStepType] = useState<string>('start')
  const [eStepConfig, setEStepConfig] = useState<StepConfig>()
  const [ldapFormLoading, setLdapFormLoading] = useState<boolean>(false)
  const [ldapLogin] = useLdapLoginMutation()
  const onStepConfigChanged = useCallback((stepConfig: EnrollmentStepConfig) => {
    if (stepConfig.config.stepType === 'start') {
      updateStep({ stepId: stepConfig.config.stepId, body: null }).unwrap().then(() => {
        getCurrentStep(null)
      }).catch(error => setErrorMessage(error.message))
    } else {

      setStepType(stepConfig.config.stepType)
      setStepId(stepConfig.config.stepId)

      // Get the workflow ID from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const workflowId = urlParams.get('workflowId');

      if (workflowId) {
        // Initialize form with workflowId
        form.setFieldsValue({
          workflowId,
          actionType: stepConfig.config.actionType
        });
      }

      const config = JSON.parse(stepConfig.config.actionConfiguration!) as BaseAction

      setActionConfig(config)
      setActionType(stepConfig.config.actionType!)
      if (stepConfig.step) {
        if (stepConfig.step.input) {
          form.setFieldsValue(stepConfig.step.input)
        }
        if (stepConfig.step.variables) {
          setVariables(stepConfig.step.variables)
        }
      }
      form.setFieldValue('actionType', stepConfig.config.actionType!)
      setEStepConfig(stepConfig.config)
    }
  }, [form])

  useEffect(() => {
    getCurrentStep(null)
  }, [])

  useEffect(() => {
    if (!currentStepConfig) {
      return
    }
    onStepConfigChanged(currentStepConfig)
  }, [currentStepConfig])

  useEffect(() => {
    if (!stepConfig || !isSuccess) {
      return
    }
    onStepConfigChanged(stepConfig)
  }, [stepConfig])

  const handleLdapContinue = async (username: string, password: string) => {
    try {
      setDisableContinue(true);
      const ldapAction = (actionConfig as LDAPActionConfig)
      const payload = {
        workflowId: ldapAction.workflowId,
        payload: {
          id: ldapAction.directoryServerProfileId,
          username: username,
          password: password
        }
      };
      await ldapLogin(payload).unwrap();
      setDisableContinue(false);
      return true
    } catch (error: any) {
      let errorMessage =
      $t({ defaultMessage: 'Authentication failed!'} );
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.data?.errors && error.data.errors.length > 0) {
        errorMessage = error.data.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
      setDisableContinue(false);
      return false
    }
  }

  const onFinish = async (values: ActionInput) => {
    if (values.actionType === 'LDAP_AUTH_PROVIDER') {
      setLdapFormLoading(true);
      const success = await handleLdapContinue((values as LdapInput)?.username, (values as LdapInput)?.password);
      if (success) {
        getStep({ stepId, nav: direction })
      }
      setLdapFormLoading(false);
      return;
    }
    try {
      await updateStep({ stepId, body: values }).unwrap();
      form.resetFields();
      getStep({ stepId, nav: direction })
    } catch (error) {
      console.error(error)
      setErrorMessage($t({ defaultMessage: 'Unable to finish operation' }))
    }
  }

  const handleOnBack = () => {
    setDisableContinue(false)
    setDirection('prev')
    try {
      getStep({ stepId, nav: 'prev' })
    } catch (error) {
      console.error(error)
      setErrorMessage('Unable to get previous step')
    }
  }

  const handleContinue = (cont: Boolean) => {
    setDirection(cont ? 'next' : 'prev')
    if (actionType !== 'IDP_AUTH_PROVIDER') {
      form.submit()
    }
  }

  const getImageSize = (s: string) => {
    if (s === 'SMALL') return 105
    else if (s == 'LARGE') return 105 * 2.25
    return 105 * 1.5
  }

  return (
    <>
      <Form
        form={form}
        name="userForm"
        onFinish={onFinish}
        data-testid='enrollment-form'
        layout='vertical'>

        <FormItem hidden name={'actionType'}>
          <Input value={actionType} />
        </FormItem>

        <FormItem hidden name={'workflowId'}>
          <Input />
        </FormItem>

        <Space direction='vertical' align='center' style={{ width: '100%' }}>
          {uiStyleConfig.wifi4EuNetworkId && <Wifi4euBanner/>}
          {
            uiStyleConfig.logoImage &&
            <Image preview={false} placeholder width={getImageSize(uiStyleConfig.logoSize)} height={getImageSize(uiStyleConfig.logoSize)} src={uiStyleConfig.logoImage}/>
          }
          {(actionConfig?.displayTitle !== false && actionConfig?.title)
            && <Typography.Title
              style={{
                textAlign: 'center',
                fontSize: uiStyleConfig.headerFontSize ? uiStyleConfig.headerFontSize : 16,
                color: uiColorConfig.fontHeaderColor
              }}
            >
              {actionConfig.title}
            </Typography.Title>}
          { Action(actionType, stepType, actionConfig!, variables, form, setDisableContinue, handleContinue, eStepConfig, ldapFormLoading) }
          { (actionType !== 'IDP_AUTH_PROVIDER') && <Space>
            {(stepType === 'basic' || stepType === 'split') &&
              <>
                {(firstActionStepId !== stepId)  && <Button style={{backgroundColor: uiColorConfig.buttonColor, color: uiColorConfig.buttonFontColor}} onClick={handleOnBack}>{$t({ defaultMessage: 'Back'})}</Button> }

                { (stepType === 'basic') && <Button type="primary" style={{backgroundColor: uiColorConfig.buttonColor, color: uiColorConfig.buttonFontColor}} onClick={() => handleContinue(true)} disabled={disableContinue}>
                  {(firstActionStepId !== stepId) ? $t({ defaultMessage: 'Continue'}) : $t({ defaultMessage: 'Start'})}
                </Button>
                }
              </>
            }
          </Space> }
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        </Space>
      </Form>
      <Spin spinning={isLoadingStep || isUpdatingStep} fullscreen data-testid='spinner' />
    </>
  )
}

export default EnrollmentForm
